import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CampaignAccessApiError } from "./campaignAccessApi";

const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const DRAFT_SCHEMA_VERSION = "campaign_uat_questionnaire_draft_mutation.v2";
const DRAFT_RESULT_SCHEMA_VERSION =
  "campaign_uat_questionnaire_draft_mutation_result.v2";
const RESPONSE_SUBMIT_SCHEMA_VERSION =
  "campaign_uat_questionnaire_response_submit.v2";
const RESPONSE_RECEIPT_SCHEMA_VERSION =
  "campaign_uat_questionnaire_response_receipt.v2";
const CLEAR_CONFIRMATION = "clear_testerson_2_multi_answer_saved_draft";
const SUBMIT_CONFIRMATION =
  "SUBMIT_TESTERSON_2_MULTI_ANSWER_RESPONSE_REHEARSAL";
const GENERIC_DRAFT_ERROR =
  "The synthetic multi-answer draft is temporarily unavailable.";
const GENERIC_RESPONSE_ERROR =
  "The synthetic multi-answer response could not be submitted.";

const canonicalUuidV4 = () => {
  if (typeof window.crypto?.randomUUID !== "function") return null;
  const value = String(window.crypto.randomUUID()).toLowerCase();
  return UUID_V4_PATTERN.test(value) ? value : null;
};

const utf8ByteLength = (value) => {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint <= 0x7f) bytes += 1;
    else if (codePoint <= 0x7ff) bytes += 2;
    else if (codePoint <= 0xffff) bytes += 3;
    else bytes += 4;
  }
  return bytes;
};

const containsUnpairedSurrogate = (value) => {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return true;
      index += 1;
    } else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) return true;
  }
  return false;
};

const containsDisallowedText = (value, multiline) => {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (
      ((codePoint >= 0 && codePoint <= 0x1f)
        && !(multiline && codePoint === 0x0a))
      || (codePoint >= 0x7f && codePoint <= 0x9f)
      || codePoint === 0x061c
      || codePoint === 0x200e
      || codePoint === 0x200f
      || codePoint === 0x2028
      || codePoint === 0x2029
      || (codePoint >= 0x202a && codePoint <= 0x202e)
      || (codePoint >= 0x2066 && codePoint <= 0x2069)
    ) return true;
  }
  return false;
};

const canonicalDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
    && value >= "1900-01-01"
    && value <= "2100-12-31";
};

const canonicalText = (rawValue, multiline) => {
  const lineNormalized = multiline
    ? rawValue.replace(/\r\n?/g, "\n")
    : rawValue;
  return containsUnpairedSurrogate(lineNormalized)
    ? lineNormalized
    : lineNormalized.normalize("NFC").trim();
};

const truncateToBudget = (rawValue, maximumScalars, maximumBytes) => {
  if (containsUnpairedSurrogate(rawValue)) return rawValue;
  const characters = Array.from(rawValue.normalize("NFC")).slice(0, maximumScalars);
  while (characters.length && utf8ByteLength(characters.join("")) > maximumBytes) {
    characters.pop();
  }
  return characters.join("");
};

const validationMessage = (question, value) => {
  if (["text", "textarea"].includes(question.responseType)) {
    const multiline = question.responseType === "textarea";
    const canonical = canonicalText(String(value), multiline);
    if (!canonical) return "Leave this blank or enter a value.";
    if (containsUnpairedSurrogate(canonical)) {
      return "This value contains an invalid character.";
    }
    if (containsDisallowedText(canonical, multiline)) {
      return multiline
        ? "Use plain text without control or directional characters."
        : "Use one line without control or directional characters.";
    }
    if (Array.from(canonical).length > question.answerConstraints.maxCharacters) {
      return `Use no more than ${question.answerConstraints.maxCharacters} characters.`;
    }
    if (utf8ByteLength(canonical) > question.answerConstraints.maxUtf8Bytes) {
      return `This value exceeds the ${question.answerConstraints.maxUtf8Bytes}-byte limit.`;
    }
  }
  if (question.responseType === "date" && !canonicalDate(String(value))) {
    return "Enter a real date from 1900-01-01 through 2100-12-31.";
  }
  if (["dropdown", "radio"].includes(question.responseType)
    && !question.options.some((option) => option.optionRef === value)) {
    return "Choose one of the available options.";
  }
  if (question.responseType === "checkbox" && typeof value !== "boolean") {
    return "Select or clear this checkbox explicitly.";
  }
  return "";
};

const flattenQuestions = (preview) => preview.questionnaire.sections.flatMap(
  (section) => section.questions,
);

const cloneAnswer = (answer) => ({
  questionRef: answer.questionRef,
  responseType: answer.responseType,
  value: answer.value,
});

const purgeAnswerList = (answers) => {
  answers?.forEach((answer) => {
    if (typeof answer?.value === "string") answer.value = "";
  });
};

const clearControl = (node) => {
  if (!node) return;
  if (["checkbox", "radio"].includes(node.type)) {
    node.checked = false;
    node.defaultChecked = false;
    node.removeAttribute("checked");
    return;
  }
  node.value = "";
  node.defaultValue = "";
  node.removeAttribute("value");
};

const exactPreview = (preview, current) => Boolean(
  preview?.schemaVersion === "campaign_uat_public_questionnaire_preview.v5"
  && preview?.questionnaire?.answerSetRef
  && preview?.draftPersistence?.schemaVersion
    === "campaign_uat_public_questionnaire_draft_persistence.v2"
  && preview?.responseRehearsal?.schemaVersion
    === "campaign_uat_public_questionnaire_response_rehearsal.v2"
  && current?.schemaVersion === "campaign_uat_questionnaire_draft_state.v2"
  && current.answerSetRef === preview.questionnaire.answerSetRef
);

export default function CampaignPersistedMultiAnswerDraft({
  preview,
  currentRef,
  expiresAt,
  onMutate,
  onApplied,
  onSubmit,
  onSubmitted,
  onReload,
  onTerminal,
}) {
  const currentValue = currentRef?.current;
  const valid = exactPreview(preview, currentValue);
  const current = valid ? {
    schemaVersion: currentValue.schemaVersion,
    state: currentValue.state,
    revision: currentValue.revision,
    changedAt: currentValue.changedAt,
    answerSetRef: currentValue.answerSetRef,
    answerCount: currentValue.answers.length,
  } : null;
  const questions = useMemo(
    () => (valid ? flattenQuestions(preview) : []),
    [preview, valid],
  );
  const answerSetRef = valid ? preview.questionnaire.answerSetRef : "";
  const valuesRef = useRef(new Map());
  const savedAnswersRef = useRef([]);
  const controlNodesRef = useRef(new Map());
  const reviewNodesRef = useRef(new Map());
  const pendingRef = useRef(null);
  const responsePendingRef = useRef(null);
  const actionGuardRef = useRef(false);
  const mountedRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [issues, setIssues] = useState({});
  const [publicError, setPublicError] = useState("");
  const [phase, setPhase] = useState("idle");
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);

  const controlKey = (questionRef, optionRef = "") => (
    optionRef ? `${questionRef}:${optionRef}` : questionRef
  );

  const answerFor = useCallback(
    (questionRef) => valuesRef.current.get(questionRef),
    [],
  );

  const syncQuestionControls = useCallback((question) => {
    const answer = answerFor(question.questionRef);
    if (question.responseType === "radio") {
      question.options.forEach((option) => {
        const node = controlNodesRef.current.get(
          controlKey(question.questionRef, option.optionRef),
        );
        if (node) node.checked = answer?.value === option.optionRef;
      });
      return;
    }
    const node = controlNodesRef.current.get(question.questionRef);
    if (!node) return;
    if (question.responseType === "checkbox") {
      node.checked = answer?.value === true;
    } else {
      node.value = typeof answer?.value === "string" ? answer.value : "";
    }
  }, [answerFor]);

  const controlCallbacks = useMemo(() => {
    const callbacks = new Map();
    questions.forEach((question) => {
      const optionRefs = question.responseType === "radio"
        ? question.options.map((option) => option.optionRef)
        : [""];
      optionRefs.forEach((optionRef) => {
        const key = controlKey(question.questionRef, optionRef);
        callbacks.set(key, (node) => {
          const previous = controlNodesRef.current.get(key);
          if (previous && previous !== node) clearControl(previous);
          if (!node) {
            controlNodesRef.current.delete(key);
            return;
          }
          controlNodesRef.current.set(key, node);
          syncQuestionControls(question);
        });
      });
    });
    return callbacks;
  }, [questions, syncQuestionControls]);

  const captureControl = useCallback((question, optionRef = "") => (
    controlCallbacks.get(controlKey(question.questionRef, optionRef))
  ), [controlCallbacks]);

  const displayValue = useCallback((question, answer) => {
    if (!answer) return "No answer";
    if (question.responseType === "checkbox") return answer.value ? "Yes" : "No";
    if (["dropdown", "radio"].includes(question.responseType)) {
      return question.options.find((option) => option.optionRef === answer.value)?.label
        || "Unavailable choice";
    }
    return String(answer.value);
  }, []);

  const captureReviewNode = useCallback((question) => (node) => {
    const previous = reviewNodesRef.current.get(question.questionRef);
    if (previous && previous !== node) previous.textContent = "";
    if (!node) {
      reviewNodesRef.current.delete(question.questionRef);
      return;
    }
    reviewNodesRef.current.set(question.questionRef, node);
    node.textContent = displayValue(question, answerFor(question.questionRef));
  }, [answerFor, displayValue]);

  const clearReviewNodes = useCallback(() => {
    reviewNodesRef.current.forEach((node) => { node.textContent = ""; });
  }, []);

  const purge = useCallback(() => {
    controlNodesRef.current.forEach(clearControl);
    clearReviewNodes();
    purgeAnswerList(Array.from(valuesRef.current.values()));
    purgeAnswerList(savedAnswersRef.current);
    purgeAnswerList(pendingRef.current?.answers);
    purgeAnswerList(pendingRef.current?.mutation?.answers);
    valuesRef.current.clear();
    savedAnswersRef.current = [];
    pendingRef.current = null;
    responsePendingRef.current = null;
    actionGuardRef.current = false;
  }, [clearReviewNodes]);

  useEffect(() => {
    mountedRef.current = true;
    const clearForLifecycle = () => purge();
    window.addEventListener("pagehide", clearForLifecycle);
    window.addEventListener("pageshow", clearForLifecycle);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("pagehide", clearForLifecycle);
      window.removeEventListener("pageshow", clearForLifecycle);
      purge();
    };
  }, [purge]);

  useLayoutEffect(() => {
    purge();
    const sourceCurrent = currentRef?.current;
    const initialAnswers = valid && sourceCurrent?.state === "saved"
      ? sourceCurrent.answers.map(cloneAnswer)
      : [];
    savedAnswersRef.current = initialAnswers.map(cloneAnswer);
    valuesRef.current = new Map(
      initialAnswers.map((answer) => [answer.questionRef, cloneAnswer(answer)]),
    );
    questions.forEach(syncQuestionControls);
    clearReviewNodes();
    pendingRef.current = null;
    responsePendingRef.current = null;
    actionGuardRef.current = false;
    setDirty(false);
    setIssues({});
    setPublicError("");
    setPhase("idle");
    setConfirmingClear(false);
    setConfirmingSubmit(false);
  }, [
    answerSetRef,
    clearReviewNodes,
    currentRef,
    current?.revision,
    current?.state,
    purge,
    questions,
    syncQuestionControls,
    valid,
  ]);

  if (!valid) return null;

  const canonicalAnswers = ({ requireComplete = false } = {}) => {
    const nextIssues = {};
    const answers = [];
    questions.forEach((question) => {
      const currentAnswer = answerFor(question.questionRef);
      if (!currentAnswer) {
        if (requireComplete && question.required) {
          nextIssues[question.questionRef] = question.responseType === "checkbox"
            ? "This required confirmation must be checked."
            : "This question is required before submission.";
        }
        return;
      }
      let value = currentAnswer.value;
      if (["text", "textarea"].includes(question.responseType)) {
        value = canonicalText(String(value), question.responseType === "textarea");
        if (!value) {
          if (requireComplete && question.required) {
            nextIssues[question.questionRef] =
              "This question is required before submission.";
          }
          return;
        }
      }
      const issue = validationMessage(question, value);
      if (issue) {
        nextIssues[question.questionRef] = issue;
        return;
      }
      if (requireComplete && question.responseType === "checkbox"
        && question.required && value !== true) {
        nextIssues[question.questionRef] =
          "This required confirmation must be checked.";
        return;
      }
      answers.push({
        questionRef: question.questionRef,
        responseType: question.responseType,
        value,
      });
    });
    if (utf8ByteLength(JSON.stringify(answers))
      > preview.draftPersistence.maxCanonicalPayloadUtf8Bytes) {
      nextIssues._form = "The saved answers exceed the rehearsal payload limit.";
    }
    return { answers, nextIssues };
  };

  const setQuestionAnswer = (question, value, answered = true) => {
    if (answered) {
      valuesRef.current.set(question.questionRef, {
        questionRef: question.questionRef,
        responseType: question.responseType,
        value,
      });
    } else {
      valuesRef.current.delete(question.questionRef);
    }
    const { nextIssues } = canonicalAnswers();
    setIssues(nextIssues);
    setDirty(true);
    setPublicError("");
    clearReviewNodes();
    setConfirmingClear(false);
    setConfirmingSubmit(false);
  };

  const resultMatchesMutation = (result, pending) => Boolean(
    result
    && result.schemaVersion === DRAFT_RESULT_SCHEMA_VERSION
    && result.state === (pending.mutation.action === "save" ? "saved" : "cleared")
    && result.revision === pending.mutation.expectedRevision + 1
    && result.expiresAt === expiresAt
    && result.answerSetRef === answerSetRef
    && result.answerCount === (pending.mutation.action === "save"
      ? pending.answers.length
      : 0)
    && typeof result.changedAt === "string"
    && Number.isFinite(new Date(result.changedAt).getTime())
    && new Date(result.changedAt).getTime() < new Date(expiresAt).getTime()
    && typeof result.idempotentReplay === "boolean"
  );

  const runPendingMutation = async (pending, retrying = false) => {
    if (actionGuardRef.current || pendingRef.current !== pending) return;
    actionGuardRef.current = true;
    clearReviewNodes();
    setConfirmingClear(false);
    setPublicError("");
    setPhase(pending.mutation.action === "save" ? "saving" : "clearing");
    try {
      const result = await onMutate(pending.mutation);
      if (!mountedRef.current || pendingRef.current !== pending) return;
      if (!resultMatchesMutation(result, pending)) {
        purge();
        onTerminal(new CampaignAccessApiError());
        return;
      }
      const appliedAnswers = pending.mutation.action === "save"
        ? pending.answers.map(cloneAnswer)
        : [];
      if (pending.mutation.action === "clear") {
        pendingRef.current = null;
        purge();
      }
      onApplied({
        result,
        savedAnswers: appliedAnswers,
        answerSetRef,
      });
      if (pending.mutation.action === "save") {
        purgeAnswerList(appliedAnswers);
        purgeAnswerList(pending.answers);
        purgeAnswerList(pending.mutation.answers);
        pendingRef.current = null;
      }
    } catch (error) {
      if (!mountedRef.current || pendingRef.current !== pending) return;
      const apiError = error instanceof CampaignAccessApiError ? error : null;
      if (apiError?.transportFailure && !retrying) {
        setPublicError("The save result is unknown. Retry the exact same request once.");
        setPhase(pending.mutation.action === "save" ? "retry_save" : "retry_clear");
      } else if (apiError?.draftConflict) {
        purge();
        setPublicError(apiError.publicMessage || GENERIC_DRAFT_ERROR);
        setPhase("conflict");
      } else if (apiError?.draftValidation) {
        pendingRef.current = null;
        setPublicError(apiError.publicMessage || GENERIC_DRAFT_ERROR);
        setPhase("idle");
      } else {
        purge();
        onTerminal(apiError || new CampaignAccessApiError());
      }
    } finally {
      actionGuardRef.current = false;
    }
  };

  const startSave = () => {
    if (phase !== "idle" || !dirty || pendingRef.current) return;
    const { answers, nextIssues } = canonicalAnswers();
    if (!answers.length) nextIssues._form = "Enter at least one answer before saving.";
    setIssues(nextIssues);
    if (Object.keys(nextIssues).length) return;
    const idempotencyKey = canonicalUuidV4();
    if (!idempotencyKey) {
      setPublicError(GENERIC_DRAFT_ERROR);
      return;
    }
    valuesRef.current = new Map(
      answers.map((answer) => [answer.questionRef, cloneAnswer(answer)]),
    );
    questions.forEach(syncQuestionControls);
    const mutation = Object.freeze({
      schemaVersion: DRAFT_SCHEMA_VERSION,
      action: "save",
      answerSetRef,
      idempotencyKey,
      expectedRevision: current.revision,
      answers: Object.freeze(answers.map(cloneAnswer)),
    });
    const pending = { mutation, answers: answers.map(cloneAnswer) };
    pendingRef.current = pending;
    runPendingMutation(pending);
  };

  const startClear = () => {
    if (phase !== "idle" || current.state !== "saved" || pendingRef.current) return;
    const idempotencyKey = canonicalUuidV4();
    if (!idempotencyKey) {
      setPublicError(GENERIC_DRAFT_ERROR);
      return;
    }
    const mutation = Object.freeze({
      schemaVersion: DRAFT_SCHEMA_VERSION,
      action: "clear",
      answerSetRef,
      idempotencyKey,
      expectedRevision: current.revision,
      confirmation: CLEAR_CONFIRMATION,
    });
    const pending = { mutation, answers: [] };
    pendingRef.current = pending;
    runPendingMutation(pending);
  };

  const resetUnsaved = () => {
    if (phase !== "idle" || !dirty) return;
    valuesRef.current.forEach((answer) => {
      if (typeof answer?.value === "string") answer.value = "";
    });
    valuesRef.current = new Map(savedAnswersRef.current.map(
      (answer) => [answer.questionRef, cloneAnswer(answer)],
    ));
    questions.forEach(syncQuestionControls);
    clearReviewNodes();
    setDirty(false);
    setIssues({});
    setPublicError("");
    setConfirmingClear(false);
    setConfirmingSubmit(false);
  };

  const responseResultMatches = (result, pending) => Boolean(
    result
    && result.schemaVersion === RESPONSE_RECEIPT_SCHEMA_VERSION
    && result.state === "submitted"
    && result.responseRevision === 1
    && result.answerCount === pending.answerCount
    && result.answerSetRef === answerSetRef
    && typeof result.submittedAt === "string"
    && Number.isFinite(new Date(result.submittedAt).getTime())
    && result.reviewedAt === null
    && typeof result.idempotentReplay === "boolean"
  );

  const runPendingSubmit = async (pending, retrying = false) => {
    if (actionGuardRef.current || responsePendingRef.current !== pending) return;
    actionGuardRef.current = true;
    setPublicError("");
    setPhase("submitting");
    try {
      const result = await onSubmit(pending.mutation);
      if (!mountedRef.current || responsePendingRef.current !== pending) return;
      if (!responseResultMatches(result, pending)) {
        purge();
        onTerminal(new CampaignAccessApiError());
        return;
      }
      purge();
      onSubmitted(result);
    } catch (error) {
      if (!mountedRef.current || responsePendingRef.current !== pending) return;
      const apiError = error instanceof CampaignAccessApiError ? error : null;
      if (apiError?.transportFailure && !retrying) {
        setPublicError("The submit result is unknown. Retry the exact same request once.");
        setPhase("retry_submit");
      } else if (apiError?.responseConflict) {
        purge();
        onReload();
      } else if (apiError?.responseValidation) {
        responsePendingRef.current = null;
        clearReviewNodes();
        setConfirmingSubmit(false);
        setPublicError(apiError.publicMessage || GENERIC_RESPONSE_ERROR);
        setPhase("idle");
      } else {
        purge();
        onTerminal(apiError || new CampaignAccessApiError());
      }
    } finally {
      actionGuardRef.current = false;
    }
  };

  const startResponseReview = () => {
    if (phase !== "idle" || dirty || current.state !== "saved") return;
    const { answers, nextIssues } = canonicalAnswers({ requireComplete: true });
    if (answers.length !== savedAnswersRef.current.length) {
      nextIssues._form = nextIssues._form
        || "Resolve the required answers before submission.";
    }
    setIssues(nextIssues);
    if (Object.keys(nextIssues).length) return;
    setPublicError("");
    setConfirmingClear(false);
    setConfirmingSubmit(true);
  };

  const confirmResponseSubmit = () => {
    if (
      phase !== "idle"
      || !confirmingSubmit
      || dirty
      || current.state !== "saved"
      || current.revision < 1
      || responsePendingRef.current
    ) return;
    const { answers, nextIssues } = canonicalAnswers({ requireComplete: true });
    setIssues(nextIssues);
    if (Object.keys(nextIssues).length || answers.length !== savedAnswersRef.current.length) {
      clearReviewNodes();
      setConfirmingSubmit(false);
      return;
    }
    const idempotencyKey = canonicalUuidV4();
    if (!idempotencyKey) {
      setPublicError(GENERIC_RESPONSE_ERROR);
      return;
    }
    const mutation = Object.freeze({
      schemaVersion: RESPONSE_SUBMIT_SCHEMA_VERSION,
      action: "submit",
      confirmation: SUBMIT_CONFIRMATION,
      answerSetRef,
      expectedDraftRevision: current.revision,
      idempotencyKey,
    });
    const pending = { mutation, answerCount: answers.length };
    responsePendingRef.current = pending;
    clearReviewNodes();
    runPendingSubmit(pending);
  };

  const processing = [
    "saving",
    "clearing",
    "submitting",
    "retry_save",
    "retry_clear",
    "retry_submit",
    "conflict",
  ].includes(phase);
  const statusLabel = current.state === "saved"
    ? `Saved draft · revision ${current.revision} · ${current.answerCount} answers`
    : current.state === "cleared"
      ? `Cleared draft · revision ${current.revision}`
      : "No saved draft · revision 0";

  const textControlProps = {
    autoCapitalize: "off",
    autoComplete: "off",
    autoCorrect: "off",
    onCopy: (event) => event.preventDefault(),
    onCut: (event) => event.preventDefault(),
    onPaste: (event) => event.preventDefault(),
    spellCheck: false,
  };

  const renderControl = (question) => {
    const commonClass = "mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 disabled:bg-slate-100";
    const disabled = processing || confirmingSubmit;
    if (["text", "textarea"].includes(question.responseType)) {
      const multiline = question.responseType === "textarea";
      const Control = multiline ? "textarea" : "input";
      return (
        <Control
          {...textControlProps}
          aria-describedby={`campaign-multi-answer-issue-${question.questionRef}`}
          className={commonClass}
          disabled={disabled}
          id={`campaign-multi-answer-${question.questionRef}`}
          onChange={(event) => {
            const constraints = question.answerConstraints;
            const lineNormalized = multiline
              ? event.currentTarget.value.replace(/\r\n?/g, "\n")
              : event.currentTarget.value;
            const value = truncateToBudget(
              lineNormalized,
              constraints.maxCharacters,
              constraints.maxUtf8Bytes,
            );
            if (event.currentTarget.value !== value) event.currentTarget.value = value;
            setQuestionAnswer(question, value, value.length > 0);
          }}
          onKeyDown={multiline ? undefined : (event) => {
            if (event.key === "Enter") event.preventDefault();
          }}
          ref={captureControl(question)}
          rows={multiline ? 5 : undefined}
          type={multiline ? undefined : "text"}
        />
      );
    }
    if (question.responseType === "date") {
      const issueId = `campaign-multi-answer-issue-${question.questionRef}`;
      const guidanceId = `campaign-multi-answer-date-guidance-${question.questionRef}`;
      return (
        <>
          <input
            aria-describedby={`${guidanceId} ${issueId}`}
            aria-invalid={Boolean(issues[question.questionRef])}
            className={commonClass}
            disabled={disabled}
            id={`campaign-multi-answer-${question.questionRef}`}
            max={question.answerConstraints.maxDate}
            min={question.answerConstraints.minDate}
            onChange={(event) => {
              const { value, validity } = event.currentTarget;
              if (!value && validity?.badInput) return;
              setQuestionAnswer(question, value, value.length > 0);
            }}
            onCopy={(event) => event.preventDefault()}
            onCut={(event) => event.preventDefault()}
            onPaste={(event) => event.preventDefault()}
            ref={captureControl(question)}
            type="date"
          />
          <p className="mt-1 text-xs text-slate-600" id={guidanceId}>
            Use the calendar or your browser&apos;s displayed date format.
            Allowed range: January 1, 1900 through December 31, 2100;
            it is saved as YYYY-MM-DD.
          </p>
        </>
      );
    }
    if (question.responseType === "dropdown") {
      return (
        <select
          className={commonClass}
          disabled={disabled}
          id={`campaign-multi-answer-${question.questionRef}`}
          onChange={(event) => setQuestionAnswer(
            question,
            event.currentTarget.value,
            event.currentTarget.value.length > 0,
          )}
          ref={captureControl(question)}
        >
          <option value="">Select an option</option>
          {question.options.map((option) => (
            <option key={option.optionRef} value={option.optionRef}>{option.label}</option>
          ))}
        </select>
      );
    }
    if (question.responseType === "radio") {
      return (
        <fieldset className="mt-2 space-y-2" disabled={disabled}>
          <legend className="sr-only">{question.label}</legend>
          {question.options.map((option) => (
            <label className="flex items-center gap-2 text-sm text-slate-800" key={option.optionRef}>
              <input
                name={`campaign-multi-answer-${question.questionRef}`}
                onChange={() => setQuestionAnswer(question, option.optionRef)}
                ref={captureControl(question, option.optionRef)}
                type="radio"
                value={option.optionRef}
              />
              {option.label}
            </label>
          ))}
          {!question.required && answerFor(question.questionRef) ? (
            <button
              className="text-xs font-medium text-slate-600 underline"
              onClick={() => {
                setQuestionAnswer(question, "", false);
                syncQuestionControls(question);
              }}
              type="button"
            >
              Clear choice
            </button>
          ) : null}
        </fieldset>
      );
    }
    return (
      <label className="mt-2 flex items-start gap-2 text-sm text-slate-800">
        <input
          disabled={disabled}
          id={`campaign-multi-answer-${question.questionRef}`}
          onChange={(event) => setQuestionAnswer(question, event.currentTarget.checked)}
          ref={captureControl(question)}
          type="checkbox"
        />
        <span>{question.required ? "I confirm this required statement" : "Select if applicable"}</span>
      </label>
    );
  };

  return (
    <section
      aria-labelledby="campaign-persisted-multi-answer-title"
      className="mt-5 rounded-xl border border-cyan-200 bg-cyan-50 p-5"
      data-testid="campaign-persisted-multi-answer-draft"
    >
      <h2 id="campaign-persisted-multi-answer-title" className="text-lg font-semibold text-cyan-950">
        Synthetic multi-answer rehearsal
      </h2>
      <p className="mt-2 text-sm text-cyan-950">
        Save one partial synthetic draft, then explicitly review and confirm its
        isolated rehearsal submission. Required answers are enforced at submission.
      </p>
      <p className="mt-1 text-xs text-cyan-900">
        There is no autosave, browser storage, clipboard transfer, upload,
        booking, CRM write, email, queue work, or provider call.
      </p>

      <div className="mt-4 rounded-lg border border-cyan-200 bg-white p-4">
        <p className="text-sm font-medium text-slate-900" role="status">{statusLabel}</p>
        {current.changedAt ? (
          <p className="mt-1 text-xs text-slate-600">
            Server changed this draft at {new Date(current.changedAt).toLocaleString()}.
          </p>
        ) : null}

        <div className="mt-4 space-y-5">
          {preview.questionnaire.sections.map((section) => (
            <fieldset className="rounded-lg border border-slate-200 p-4" key={section.sequence}>
              <legend className="px-1 text-sm font-semibold text-slate-900">
                Section {section.sequence}: {section.title}
              </legend>
              <div className="mt-2 space-y-4">
                {section.questions.map((question) => (
                  <div key={question.questionRef}>
                    <label
                      className="block text-sm font-medium text-slate-900"
                      htmlFor={question.responseType === "radio"
                        ? undefined
                        : `campaign-multi-answer-${question.questionRef}`}
                    >
                      {question.sequence}. {question.label}
                      {question.required ? " *" : ""}
                    </label>
                    {question.helpText ? (
                      <p className="mt-1 text-xs text-slate-600">{question.helpText}</p>
                    ) : null}
                    {renderControl(question)}
                    <p
                      className="mt-1 text-xs text-amber-800"
                      id={`campaign-multi-answer-issue-${question.questionRef}`}
                      role={issues[question.questionRef] ? "alert" : undefined}
                    >
                      {issues[question.questionRef] || ""}
                    </p>
                  </div>
                ))}
              </div>
            </fieldset>
          ))}
        </div>

        {issues._form ? (
          <p className="mt-3 text-sm text-amber-800" role="alert">{issues._form}</p>
        ) : null}
        {publicError ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
            {publicError}
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          {phase === "idle" ? (
            <>
              <button
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                disabled={!dirty}
                onClick={startSave}
                type="button"
              >
                Save partial draft
              </button>
              <button
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 disabled:opacity-50"
                disabled={!dirty}
                onClick={resetUnsaved}
                type="button"
              >
                Reset unsaved
              </button>
              {current.state === "saved" && !confirmingClear && !confirmingSubmit ? (
                <>
                  <button
                    className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                    disabled={dirty}
                    onClick={startResponseReview}
                    type="button"
                  >
                    Review synthetic response
                  </button>
                  <button
                    className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700"
                    onClick={() => setConfirmingClear(true)}
                    type="button"
                  >
                    Clear saved draft
                  </button>
                </>
              ) : null}
            </>
          ) : null}
          {phase === "saving" ? <p role="status">Saving synthetic draft…</p> : null}
          {phase === "clearing" ? <p role="status">Clearing saved draft…</p> : null}
          {phase === "submitting" ? <p role="status">Submitting synthetic response…</p> : null}
          {["retry_save", "retry_clear"].includes(phase) ? (
            <button
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white"
              onClick={() => runPendingMutation(pendingRef.current, true)}
              type="button"
            >
              Retry {phase === "retry_save" ? "save" : "clear"} draft
            </button>
          ) : null}
          {phase === "retry_submit" ? (
            <button
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white"
              onClick={() => runPendingSubmit(responsePendingRef.current, true)}
              type="button"
            >
              Retry exact response submission
            </button>
          ) : null}
          {phase === "conflict" ? (
            <button
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white"
              onClick={onReload}
              type="button"
            >
              Reload saved draft
            </button>
          ) : null}
        </div>

        {confirmingClear && phase === "idle" ? (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-900">
              Clear every live answer from this saved rehearsal draft? Revision
              evidence remains; this is not a claim of erasure from backups.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white" onClick={startClear} type="button">
                Confirm clear saved draft
              </button>
              <button className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800" onClick={() => setConfirmingClear(false)} type="button">
                Cancel clear
              </button>
            </div>
          </div>
        ) : null}

        {confirmingSubmit && phase === "idle" ? (
          <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50 p-3">
            <h3 className="text-sm font-semibold text-violet-950">
              Review before synthetic submission
            </h3>
            <p className="mt-2 text-xs text-violet-900">
              Confirming submits only this isolated rehearsal. It does not invoke
              providers, email, legacy submissions, engagement, booking, or CRM.
            </p>
            <dl className="mt-3 space-y-2 rounded-lg border border-violet-200 bg-white p-3">
              {questions.map((question) => (
                <div key={question.questionRef}>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {question.label}
                  </dt>
                  <dd
                    className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-900"
                    onCopy={(event) => event.preventDefault()}
                    onCut={(event) => event.preventDefault()}
                    ref={captureReviewNode(question)}
                  />
                </div>
              ))}
            </dl>
            <div className="mt-3 flex flex-wrap gap-2">
              <button className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white" onClick={confirmResponseSubmit} type="button">
                Confirm synthetic submission
              </button>
              <button
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800"
                onClick={() => {
                  clearReviewNodes();
                  setConfirmingSubmit(false);
                }}
                type="button"
              >
                Cancel submission
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
